import { LienNoyauEnCours } from '@/components/noyaux/lien-noyau-en-cours';
import { RondChargement } from '@/components/noyaux/noyau-en-cours';
import { infobulleNoyau } from '@/components/noyaux/noyaux-affichage';
import { PastilleNoyau } from '@/components/noyaux/pastille-noyau';
import { cn } from '@/lib/utils';
import { NOYAU_CODES, type NoyauCode } from '@rosetta/shared';
import { Link } from '@tanstack/react-router';

interface PastillesNoyauxProps {
  noyaux: readonly NoyauCode[];
  /**
   * The noyaux being added to the text (#11, H9): white pill with a dashed frame and a spinner,
   * and, when `lancements` knows the launch of the noyau (#36), a link named "<noyau>, en cours
   * de traduction" to the waiting screen of that launch; ", en cours" for a screen reader otherwise.
   */
  enCours?: readonly NoyauCode[];
  lancements?: Readonly<Partial<Record<NoyauCode, string>>>;
  /**
   * For each noyau already translated, the translation whose result its pill opens (#36): a link
   * named "<noyau> <nom>, ouvrir sa traduction", above the stretched link of the card, to
   * `/resultat/<translation>?noyau=<noyau>`. The six noyaux are then six pills, never "les six
   * noyaux" (a summary has no noyau to open).
   */
  traductions?: Readonly<Partial<Record<NoyauCode, string>>>;
}

const PUCE = 'inline-flex h-6 items-center rounded-full px-2 text-gray-800';

/**
 * The noyaux a translation targets, at the foot of a card: one pill per noyau (round dot
 * and code), or "les six noyaux" with six dots when it targets all of them (and none is being
 * added: a pill "en cours" must show which one it is).
 */
export function PastillesNoyaux({
  noyaux,
  enCours = [],
  lancements = {},
  traductions = {},
}: PastillesNoyauxProps) {
  if (
    noyaux.length === NOYAU_CODES.length &&
    enCours.length === 0 &&
    Object.keys(traductions).length === 0
  ) {
    return (
      <span
        className={`${PUCE} gap-2 self-start bg-gray-50 font-semibold text-caption leading-none`}
      >
        <span aria-hidden="true" className="inline-flex gap-1">
          {NOYAU_CODES.map((code) => (
            <PastilleNoyau key={code} noyau={code} className="size-2" />
          ))}
        </span>
        les six noyaux
      </span>
    );
  }

  return (
    <ul aria-label="noyaux visés" className="flex flex-wrap gap-2">
      {noyaux.map((code) => {
        const ajout = enCours.includes(code);
        const lancement = ajout ? lancements[code] : undefined;
        if (lancement !== undefined) {
          // A running noyau whose launch is known (#36): the whole pill is the link, above the
          // stretched link of the card (`relative z-10`).
          return (
            <li key={code} className="inline-flex">
              <LienNoyauEnCours
                noyau={code}
                lancement={lancement}
                className={cn(
                  PUCE,
                  'relative z-10 gap-1 border border-gray-500 border-dashed bg-white font-bold text-caption leading-none tracking-[1px] hover:bg-gray-100 hover:text-gray-800',
                )}
              >
                <PastilleNoyau noyau={code} className="size-2" />
                {code}
                <RondChargement />
              </LienNoyauEnCours>
            </li>
          );
        }
        const traduction = ajout ? undefined : traductions[code];
        if (traduction !== undefined) {
          // A noyau already translated (#36): its pill opens that translation, on that noyau.
          return (
            <li key={code} className="inline-flex">
              <Link
                to="/resultat/$id"
                params={{ id: traduction }}
                search={{ noyau: code }}
                aria-label={`${infobulleNoyau(code)}, ouvrir sa traduction`}
                className={cn(
                  PUCE,
                  'relative z-10 gap-1 bg-gray-50 font-bold text-caption leading-none tracking-[1px] no-underline hover:bg-gray-100 hover:text-gray-800',
                )}
              >
                <PastilleNoyau noyau={code} className="size-2" />
                {code}
              </Link>
            </li>
          );
        }
        return (
          <li
            key={code}
            className={cn(
              PUCE,
              'gap-1 font-bold text-caption leading-none tracking-[1px]',
              // The dashed frame is `gray-500`: it is what tells the pill apart (3:1, S14).
              ajout ? 'border border-gray-500 border-dashed bg-white' : 'bg-gray-50',
            )}
          >
            <PastilleNoyau noyau={code} className="size-2" />
            {code}
            {ajout ? (
              <>
                <RondChargement />
                <span className="sr-only">, en cours</span>
              </>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
